import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const BolsaAvenueCorridorRealEstatePage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=Westminster&county=Orange&state=CA&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Bolsa Avenue Corridor Real Estate | Heart of Little Saigon | Truc Doan</title>
        <meta name="description" content="Explore real estate along the Bolsa Avenue Corridor — the commercial heart of Little Saigon in Westminster, CA. Mixed-use properties, business opportunities, and vibrant shopping. Contact Truc Doan." />
        <meta name="keywords" content="Bolsa Avenue real estate, Little Saigon commercial property, Westminster Bolsa Ave homes, mixed-use property Westminster, business opportunities Little Saigon, Bolsa Avenue corridor, Truc Doan realtor" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/bolsa-avenue-corridor-real-estate`} />
        <meta property="og:title" content="Bolsa Avenue Corridor Real Estate | Heart of Little Saigon | Truc Doan" />
        <meta property="og:description" content="Invest in the commercial heart of Little Saigon. Bolsa Avenue corridor properties, business opportunities, and homes in Westminster's most vibrant area." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/bolsa-avenue-corridor-real-estate`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Westminster, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": "+1-818-818-0989",
            "email": "doanestate@gmail.com",
            "url": `${SEO_CONFIG.DOMAIN}/bolsa-avenue-corridor-real-estate`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Truc Doan specializes in Bolsa Avenue corridor real estate — the commercial heart of Little Saigon in Westminster, CA.",
            "areaServed": {
              "@type": "City",
              "name": "Westminster, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button
          onClick={() => navigate('/')}
          style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}
        >
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>Bolsa Avenue Corridor Real Estate</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Own property in the commercial heart of Little Saigon — Orange County's most vibrant cultural and business corridor.
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>The Heart of Little Saigon</h2>
          <p>Bolsa Avenue stretches through Westminster as the undisputed center of Little Saigon — the largest Vietnamese commercial district outside of Vietnam. With hundreds of businesses, restaurants, shopping centers, and professional offices, this corridor is a thriving economic engine that attracts visitors and investors from around the world.</p>
          <p>Properties along and near Bolsa Avenue benefit from exceptional foot traffic, strong business demand, and a community that takes immense pride in its commercial district. Whether you're looking for a home near the action or an investment property, Bolsa Avenue offers unmatched opportunity.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>Bolsa Avenue Corridor Highlights</h2>
          <p>From Asian Garden Mall to Phước Lộc Thọ, the Bolsa corridor offers diverse real estate opportunities:</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>Shopping Centers</h3>
              <p>Major centers like Asian Garden Mall, Little Saigon Plaza, and Bolsa Mini Mall draw thousands of shoppers daily.</p>
            </div>
            <div className="amenity">
              <h3>Business Opportunities</h3>
              <p>Retail spaces, restaurants, and professional offices in high demand. Strong foot traffic and loyal customer base.</p>
            </div>
            <div className="amenity">
              <h3>Mixed-Use Properties</h3>
              <p>Live above your business or invest in mixed-use developments combining retail on the ground floor with residential above.</p>
            </div>
            <div className="amenity">
              <h3>Vibrant Dining Scene</h3>
              <p>Over 200 restaurants serving authentic Vietnamese, Chinese, and fusion cuisine line the corridor.</p>
            </div>
            <div className="amenity">
              <h3>Cultural Landmarks</h3>
              <p>Vietnam War Memorial, cultural centers, and community gathering spaces make this area historically significant.</p>
            </div>
            <div className="amenity">
              <h3>Transit Access</h3>
              <p>OCTA bus routes, proximity to 405 & 22 freeways, and ample parking make the corridor easily accessible.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>Bolsa Avenue Corridor Market Snapshot</h2>
          <p>Properties near Bolsa Avenue command premium prices due to commercial proximity and cultural significance.</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Median Home Price</td><td>~$760,000</td></tr>
              <tr><td>Avg Days on Market</td><td>20–30 Days</td></tr>
              <tr><td>Most Active Price Range</td><td>$550K – $950K</td></tr>
              <tr><td>Property Types</td><td>Single Family, Mixed-Use, Condos</td></tr>
              <tr><td>Investment Appeal</td><td>High — Strong rental & commercial demand</td></tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Properties for Sale Near Bolsa Avenue</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>Loading properties...</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div
                  key={property.listing_key}
                  className="property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                  style={{
                    backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">{formatPropertyAddress(property)}, CA {property.postal_code}</div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>Listed by: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>Browse all available Bolsa Avenue corridor listings by clicking "Search Listings" below.</p>
          )}
        </section>

        <section className="cta-section">
          <h2>Ready to Invest in Bolsa Avenue Corridor?</h2>
          <p>Contact Truc Doan today for expert guidance on residential and commercial properties along Little Saigon's main corridor.</p>
          <div className="cta-buttons">
            <a href="mailto:doanestate@gmail.com?subject=Bolsa Avenue Corridor Real Estate Inquiry" className="cta-button primary">Email Truc Doan</a>
            <a href="tel:+1-818-818-0989" className="cta-button secondary">Call +1-818-818-0989</a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>Search Listings</button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.</span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default BolsaAvenueCorridorRealEstatePage;
